import { clearPrivateLocalData, getMeta, setMeta } from '../offline/db';

export type ProductionSummary = {
  id: string;
  title: string;
  client: string | null;
  locationSummary: string | null;
  startDate: string;
  endDate: string;
  timezone: string;
  status: 'PRE_PRODUCTION' | 'IN_PRODUCTION' | 'POST_PRODUCTION' | 'DELIVERED' | 'ARCHIVED';
  productionCompany: string | null;
  accommodationBase: string | null;
  revision: number;
};

export type TodayDeliverable = {
  id: string;
  productionDayId: string | null;
  type: string;
  title: string;
  deadline: string | null;
  status: 'NOT_STARTED' | 'EDITING' | 'REVIEW' | 'CHANGES' | 'APPROVED' | 'EXPORTED' | 'DELIVERED';
};

export type TodayRunSheetItem = {
  id: string;
  startTime: string | null;
  endTime: string | null;
  timeLabel: string | null;
  location: string | null;
  activity: string;
  priority: 'MASSIMA' | 'ALTA' | 'MEDIA' | 'BASSA' | 'TBD';
  sortOrder: number;
  assignments: Array<{
    crewId: string;
    label: string | null;
    productionRole: string;
    name: string;
  }>;
};

export type TodayPayload = {
  production: ProductionSummary;
  projectClock: {
    date: string;
    time: string;
    localDateTime: string;
  };
  day: null | {
    id: string;
    dayNumber: number;
    date: string;
    title: string | null;
    primaryLocation: string | null;
    crewCall: string | null;
    firstShot: string | null;
    plannedWrap: string | null;
    actualWrap: string | null;
    editorialObjective: string | null;
    generalNotes: string | null;
    revision: number;
  };
  nextDelivery: null | {
    deadline: string;
    overdue: boolean;
    items: TodayDeliverable[];
  };
  deliveryFocusDay: null | {
    id: string;
    dayNumber: number;
    date: string;
  };
  nextUp: TodayRunSheetItem | null;
  following: TodayRunSheetItem | null;
  priorities: Array<{
    id: string;
    title: string;
    priority: 'MASSIMA' | 'ALTA' | 'MEDIA' | 'BASSA' | 'TBD';
    status: 'OPEN' | 'DONE';
    sortOrder: number;
  }>;
  dailyDeliverables: TodayDeliverable[];
  capabilities: {
    cameraReport: boolean;
    pickup: boolean;
    interview: boolean;
    equipment: boolean;
    quickNote: boolean;
  };
};

export type ProductionCorePayload = {
  production: ProductionSummary;
  days: Array<{
    id: string;
    dayNumber: number;
    date: string;
    primaryLocation: string | null;
    crewCall: string | null;
    firstShot: string | null;
    plannedWrap: string | null;
    actualWrap: string | null;
    revision: number;
  }>;
  crew: Array<{
    id: string;
    personId: string;
    name: string;
    firstName: string;
    lastName: string | null;
    phone: string | null;
    email: string | null;
    label: string | null;
    productionRole: string;
    vehicle: string | null;
    active: boolean;
    revision: number;
  }>;
  contacts: Array<{
    id: string;
    displayName: string;
    organization: string | null;
    role: string | null;
    category: string;
    phone: string | null;
    email: string | null;
    priority: boolean;
    emergency: boolean;
  }>;
  deliverables: TodayDeliverable[];
  runsheet: Array<{
    id: string;
    productionDayId: string;
    startTime: string | null;
    endTime: string | null;
    timeLabel: string | null;
    location: string | null;
    activity: string;
    priority: string;
    sortOrder: number;
  }>;
};

type Cached<T> = { value: T; cachedAt: string };

async function fetchWithOfflineFallback<T>(url: string, cacheKey: string): Promise<{ data: T; stale: boolean }> {
  try {
    const response = await fetch(url, { credentials: 'include' });
    if (response.status === 401 || response.status === 403) {
      await clearPrivateLocalData();
      window.dispatchEvent(new CustomEvent('binder:auth-required'));
      throw new Error('AUTHORIZATION_REQUIRED');
    }
    if (!response.ok) throw new Error(`HTTP_${response.status}`);
    const data = (await response.json()) as T;
    await setMeta(cacheKey, { value: data, cachedAt: new Date().toISOString() } satisfies Cached<T>);
    return { data, stale: false };
  } catch (error) {
    if (error instanceof Error && error.message === 'AUTHORIZATION_REQUIRED') throw error;

    const offlineAuthorizedUntil = await getMeta<string>('offlineAuthorizedUntil');
    if (!offlineAuthorizedUntil || Date.parse(offlineAuthorizedUntil) <= Date.now()) throw error;

    const cached = await getMeta<Cached<T>>(cacheKey);
    if (cached) return { data: cached.value, stale: true };
    throw error;
  }
}

export function fetchToday(productionId?: string) {
  const suffix = productionId ? `?productionId=${encodeURIComponent(productionId)}` : '';
  const cacheKey = productionId ? `today:${productionId}` : 'today:active';
  return fetchWithOfflineFallback<TodayPayload>(`/api/today${suffix}`, cacheKey);
}

export async function fetchProductions(): Promise<ProductionSummary[]> {
  const { data } = await fetchWithOfflineFallback<{ productions: ProductionSummary[] }>(
    '/api/productions',
    'productions:list'
  );
  return data.productions;
}

export function fetchProductionCore(productionId: string) {
  return fetchWithOfflineFallback<ProductionCorePayload>(
    `/api/productions/${encodeURIComponent(productionId)}/core`,
    `production-core:${productionId}`
  );
}
